import { defineComponent, h } from 'vue';
import BaseComponent from './BaseComponentMixin.jsx';
import careHelpfulFunctions from '../careHelpfulFunctions.jsx';
import utils from '../../Shared/utils.jsx';
import {
    iconAffixClasses,
    BUTTON_BORDER_CLASS,
    tooltipLocationToAnchorSelf,
    tooltipOffsetForPlacement,
    mapSchemaButtonColorToQuasar,
    schemaButtonColorBaseTokenFromEvaluatedRaw,
    normalizeIconName,
    coerceSchemaBoolean,
    SENTENCE_CASE_BUTTON_CLASS,
    LITERAL_CASING_BUTTON_CLASS,
} from '../../Shared/buttonAppearance.js';

import TranslationContainer from './TranslationContainer.jsx';

var baseMixin = {
    mixins: [BaseComponent],
    data: () => ({
        iconrenderer: null,
        iconStyleEval: null,
        iconpositioner: null,
        flipHorizontallyEval: null,
        flipVerticallyEval: null,
        borderEval: null,
        animationEval: null,
        colorEval: null,
        cssClassEval: null,
        tooltiprenderer: null,
        useLiteralCasingEval: null,
    }),
    props: {
    },
    computed: {
        text() {
            // Not compiled because translation container takes care of it
            if (this.controlData.Text) return this.controlData.Text;
            else return '';
        },
        icon() {
            try {
                if (this.iconrenderer === null) this.iconrenderer = utils.compile(this, this.controlData.Icon);
                const icon = utils.evaluate(this.iconrenderer, this);
                return normalizeIconName(icon, { iconStyle: this.iconStyle });
            } catch (e) {
                utils.warn('Icon could not evaluate expression: ' + this.controlData.IconStyle + '; ' + e);
                return '';
            }
        },
        iconStyle() {
            try {
                if (this.iconStyleEval === null && this.controlData.IconStyle)
                    this.iconStyleEval = utils.compile(this, this.controlData.IconStyle);
                if (this.iconStyleEval) return utils.evaluate(this.iconStyleEval, this);
                return 'mdi';
            } catch (e) {
                utils.warn('IconStyle could not evaluate expression: ' + this.controlData.IconStyle + '; ' + e);
                return 'mdi';
            }
        },
        iconPosition() {
            try {
                if (this.iconpositioner === null && this.controlData.IconPosition)
                    this.iconpositioner = utils.compile(this, this.controlData.IconPosition);
                if (this.iconpositioner) return utils.evaluate(this.iconpositioner, this);
                return 'right';
            } catch (e) {
                utils.warn('IconPosition could not evaluate expression: ' + this.controlData.IconPosition + '; ' + e);
                return '';
            }
        },
        iconGap() {
            // This is a numeric-only field, no interpolation supported
            return this.controlData.IconGap || 10;
        },
        flipHorizontally() {
            try {
                if (this.flipHorizontallyEval === null && this.controlData.FlipHorizontally)
                    this.flipHorizontallyEval = utils.compile(this, this.controlData.FlipHorizontally);
                if (this.flipHorizontallyEval) return utils.evaluate(this.flipHorizontallyEval, this);
                return false;
            } catch (e) {
                utils.warn('flipHorizontally could not evaluate expression: ' + this.controlData.FlipHorizontally + '; ' + e);
                return false;
            }
        },
        flipVertically() {
            try {
                if (this.flipVerticallyEval === null && this.controlData.FlipVertically)
                    this.flipVerticallyEval = utils.compile(this, this.controlData.FlipVertically);
                if (this.flipVerticallyEval) return utils.evaluate(this.flipVerticallyEval, this);
                return false;
            } catch (e) {
                utils.warn('flipVertically could not evaluate expression: ' + this.controlData.FlipVertically + '; ' + e);
                return false;
            }
        },
        border() {
            try {
                if (this.borderEval === null && this.controlData.Border)
                    this.borderEval = utils.compile(this, this.controlData.Border);
                if (this.borderEval) return utils.evaluate(this.borderEval, this);
                return false;
            } catch (e) {
                utils.warn('border could not evaluate expression: ' + this.controlData.Border + '; ' + e);
                return false;
            }
        },
        animation() {
            try {
                if (this.animationEval === null && this.controlData.Animation)
                    this.animationEval = utils.compile(this, this.controlData.Animation);
                if (this.animationEval) return utils.evaluate(this.animationEval, this);
                return 'None';
            } catch (e) {
                utils.warn('animation could not evaluate expression: ' + this.controlData.Animation + '; ' + e);
                return 'None';
            }
        },
        evaluatedButtonColorRaw() {
            try {
                let theColor = 'secondary';
                if (this.colorEval === null && this.controlData.Color)
                    this.colorEval = utils.compile(this, this.controlData.Color);
                if (this.colorEval) theColor = utils.evaluate(this.colorEval, this);
                return theColor != null && theColor !== '' ? String(theColor) : 'secondary';
            } catch (e) {
                utils.warn('color could not evaluate expression: ' + this.controlData.Color + '; ' + e);
                return 'secondary';
            }
        },
        color() {
            return mapSchemaButtonColorToQuasar(this.evaluatedButtonColorRaw);
        },
        tooltip() {
            try {
                if (this.tooltiprenderer === null && this.controlData.Tooltip)
                    this.tooltiprenderer = utils.compile(this, this.Translate(this.controlData.Tooltip));
                if (this.tooltiprenderer) {
                    return utils.evaluate(this.tooltiprenderer, this);
                }
                return '';
            } catch (e) {
                utils.warn('tooltip could not evaluate expression: ' + this.controlData.Tooltip + '; ' + e);
                return '';
            }
        },
        tooltipLocation() {
            // This field does not support interpolation
            const loc = this.controlData.TooltipLocation;
            return loc == null || loc === '' ? 'top' : String(loc);
        },
        buttonFab() {
            return !!(this.controlData.Themes && this.controlData.Themes.includes('FloatingAction'));
        },
        showButtonBorderChrome() {
            return !!this.border;
        },
        qBtnFlat() {
            // Flat / Transparent / FlatWhite (variant) can coexist with FloatingAction (fab+round)
            return this.variant === 'text' || this.variant === 'plain';
        },
        qBtnOutline() {
            return this.variant === 'outlined';
        },
        isOutlined() {
            const raw = this.evaluatedButtonColorRaw;
            return !!raw && String(raw).trim().toLowerCase().startsWith('outline-');
        },
        isOutlinePrimary() {
            return this.isOutlined &&
                schemaButtonColorBaseTokenFromEvaluatedRaw(this.evaluatedButtonColorRaw) === 'primary';
        },
        align() {
            if (this.controlData.StyleHints?.TextAlign) {
                switch (this.controlData.StyleHints.TextAlign) {
                    case 'Left':
                        return 'left';
                    case 'Center':
                        return 'center';
                    case 'Right':
                        return 'right';
                }
            }

            if (this.controlData.StyleHints?.JustifyContent) {
                const jc = String(this.controlData.StyleHints.JustifyContent).toLowerCase();
                if (jc === 'flex-start' || jc === 'start' || jc === 'left')
                    return 'left';
                if (jc === 'flex-end' || jc === 'end' || jc === 'right')
                    return 'right';
                if (jc === 'center')
                    return 'center';
            }

            return undefined;
        },
        // Match Vue2: put Fixed/Min/Max size on the button, not only the wrapper.
        buttonDimensionStyle() {
            const style = {};
            const width = this.sizeOptions?.Width;
            const height = this.sizeOptions?.Height;
            const unit = (units) => units == 'Percent' ? '%' : units == 'Em' ? 'em' : 'px';

            if (width?.Mode === 'Fixed' && width.Value != null && width.Value !== '')
                style.width = `${width.Value}${unit(width.Units)}`;
            if (width?.Min)
                style.minWidth = `${width.Min}${unit(width.MinUnits)}`;
            if (width?.Max)
                style.maxWidth = `${width.Max}${unit(width.MaxUnits)}`;
            if (height?.Mode === 'Fixed' && height.Value != null && height.Value !== '')
                style.height = `${height.Value}${unit(height.Units)}`;
            if (height?.Min)
                style.minHeight = `${height.Min}${unit(height.MinUnits)}`;
            if (height?.Max)
                style.maxHeight = `${height.Max}${unit(height.MaxUnits)}`;
            return style;
        },
        buttonClasses() {
            let classes = {
                ThemeRightConnector: this.controlData.Themes ? this.controlData.Themes.includes('RightConnector') : false,
                ThemeLeftConnector: this.controlData.Themes ? this.controlData.Themes.includes('LeftConnector') : false,
                HasBackground: this.hasBackground,
                TransparentBackground: !this.hasBackground,
            };

            try {
                if (this.cssClassEval === null && this.controlData.CssClass)
                    this.cssClassEval = utils.compile(this, this.controlData.CssClass);
                if (this.cssClassEval)
                    classes[utils.evaluate(this.cssClassEval, this)] = true;
            }
            catch (e) {
                utils.warn('cssClass could not evaluate expression: ' + this.controlData.CssClass + '; ' + e);
            }

            const widthMode = this.sizeOptions?.Width?.Mode;
            const inPopupMenu = this.parentType === 'BasicPopupMenu' || this.parentType === 'IconPopupMenu';
            // Menu rows default to fill unless Width.Mode is explicitly Fixed.
            // Nested layouts often rewrite parentType; .c-BasicPopupMenu-menuHost CSS covers those.
            if (widthMode === 'Fill' || (inPopupMenu && widthMode !== 'Fixed'))
                classes['full-width'] = true;

            if (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered'))) {
                classes['q-py-none'] = true;
                classes['q-px-sm'] = true;
            }

            if (this.showButtonBorderChrome && this.variant !== 'outlined')
                classes[BUTTON_BORDER_CLASS] = true;

            classes['c-BasicButton--outline-primary'] = this.isOutlinePrimary;
            // Fixed Height: skip q-my-xs on the QBtn (overflow:hidden wrapper would clip hover /
            // focus-jump). BasicButton restores the 4px vertical nudge on the wrapper instead.
            classes['q-my-xs'] = this.sizeOptions?.Height?.Mode !== 'Fixed';

            return classes;
        },
        buttonStyles() {
            const styles = { ...this.buttonDimensionStyle };

            if (this.controlData.StyleHints && this.controlData.StyleHints.TextAlign)
                switch (this.controlData.StyleHints.TextAlign) {
                    case 'Left':
                        styles['justify-content'] = 'left';
                        break;
                    case 'Center':
                        styles['justify-content'] = 'center';
                        break;
                    case 'Right':
                        styles['justify-content'] = 'right';
                        break;
                }
            else if (this.controlData.StyleHints && this.controlData.StyleHints.JustifyContent)
                styles['justify-content'] = this.controlData.StyleHints.JustifyContent;

            return styles;
        },
        iconClasses() {
            return iconAffixClasses({
                flipH: !!this.flipHorizontally,
                flipV: !!this.flipVertically,
                animation: this.animation,
            });
        },
        iconSize() {
            if (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered')))
                return '12px';
            else
                return 'xs';
        },
        variant() {
            if (this.controlData.Themes && this.controlData.Themes.includes('Flat'))
                return 'text';
            else if (this.controlData.Themes && this.controlData.Themes.includes('FlatWhite'))
                return 'text';
            else if (this.controlData.Themes && this.controlData.Themes.includes('Transparent'))
                return 'plain';
            else if (schemaButtonColorBaseTokenFromEvaluatedRaw(this.evaluatedButtonColorRaw) === 'link')
                return 'text';
            else if (this.isOutlined || this.controlData.Themes?.includes('CondensedBordered'))
                return 'outlined';
            // Match Vue2 BasicButton: Condensed uses text/flat chrome (colored icon, no filled bg)
            else if (this.controlData.Themes?.includes('Condensed'))
                return 'text';
            else
                return 'flat';
        },
        hasBackground() {
            const themes = this.controlData?.Themes;
            const hasThemes = themes?.length > 0;
            return !hasThemes || 
                themes?.includes('Flat') || 
                themes?.includes('FlatWhite') || 
                themes?.includes('FloatingAction');
        },
        size() {
            if (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered')))
                return '12px';
            else
                return '14px';
        },
        iconprop() {
            return this.icon && !this.text && (this.variant == 'plain' || this.variant == 'text') ? this.icon : undefined;
        },
        density() {
            if ((this.icon && !this.text && (this.variant == 'plain' || this.variant == 'text')) || (this.controlData.Themes && (this.controlData.Themes.includes('Condensed') || this.controlData.Themes.includes('CondensedBordered'))))
                return true;
            else
                return false;
        },
        /**
         * When true, keep ControlData.Text casing as authored (skip App.vue sentence-case CSS).
         * Schema: UseLiteralCasing (default false).
         */
        useLiteralCasing() {
            try {
                if (this.useLiteralCasingEval === null && this.controlData.UseLiteralCasing)
                    this.useLiteralCasingEval = utils.compile(this, this.controlData.UseLiteralCasing);
                if (this.useLiteralCasingEval)
                    return coerceSchemaBoolean(utils.evaluate(this.useLiteralCasingEval, this));
                return false;
            } catch (e) {
                utils.warn('UseLiteralCasing could not evaluate expression: ' + this.controlData.UseLiteralCasing + '; ' + e);
                return false;
            }
        },
        /** Classes for QBtn / wrappers that opt into sentence case (PopupMenuButton). */
        sentenceCaseButtonClasses() {
            if (this.useLiteralCasing)
                return { [LITERAL_CASING_BUTTON_CLASS]: true };
            return { [SENTENCE_CASE_BUTTON_CLASS]: true };
        },
    },
    methods: {
        renderButtonTooltip() {
            if (!this.tooltip) return null;
            const { anchor, self } = tooltipLocationToAnchorSelf(this.tooltipLocation);
            const offset = tooltipOffsetForPlacement(this.tooltipLocation);
            return (
                <QTooltip
                    transition-show="scale"
                    transition-hide="scale"
                    anchor={anchor}
                    self={self}
                    {...(offset !== undefined ? { offset } : {})}
                >
                    {utils.renderTooltipContent(this.tooltip)}
                </QTooltip>
            );
        },
        getButtonContent() {
            let content = [];

            if (this.text && this.icon) {
                if ((this.iconPosition || '').toLowerCase() === 'left')
                    content.push(
                        <QIcon
                            style={{ 'margin-right': this.iconGap + 'px' }}
                            name={this.icon}
                            size={this.iconSize}
                            class={this.iconClasses}
                        />
                    );
                content.push(<TranslationContainer context={this} value={this.text}></TranslationContainer>);
                if (!this.iconPosition || (this.iconPosition || '').toLowerCase() === 'right')
                    content.push(
                        <QIcon
                            style={{ 'margin-left': this.iconGap + 'px' }}
                            name={this.icon}
                            size={this.iconSize}
                            class={this.iconClasses}
                        />
                    );
            }
            else if (this.text)
                content.push(<TranslationContainer context={this} value={this.text}></TranslationContainer>);
            else if (this.icon) {
                // Explicit 20px matches Vue2 icon-only Flat buttons; QBtn icon= prop scales too large.
                if (this.styles.color)
                    content.push(
                        <QIcon name={this.icon} class={this.iconClasses} style={{ color: this.styles.color }} size="20px" />
                    );
                else
                    content.push(
                        <QIcon name={this.icon} class={this.iconClasses} size="20px" />
                    );
            }
            return content;
        },
    }
};

export default baseMixin;